import { useEffect, useState } from "react";

export interface ChartTheme {
  readonly mobile: string;
  readonly telegram: string;
  readonly grid: string;
  readonly axis: string;
  readonly tooltipBg: string;
  readonly tooltipBorder: string;
  readonly tooltipFg: string;
}

const CHART_TOKEN_VARS: Record<keyof ChartTheme, string> = {
  mobile: "--chart-series-mobile",
  telegram: "--chart-series-telegram",
  grid: "--chart-grid",
  axis: "--chart-axis",
  tooltipBg: "--chart-tooltip-bg",
  tooltipBorder: "--chart-tooltip-border",
  tooltipFg: "--chart-tooltip-fg",
};

/**
 * Fallback only for an environment with no applied stylesheet (the jsdom
 * test environment, which never loads `global.css` — `vitest.config.ts`
 * `css: true` processes CSS Modules but no test imports the global
 * tokens file — and the instant before first paint). Mirrors
 * `tokens.primitive.css`'s light-mode `--chart-blue-500` /
 * `--chart-orange-500` and `tokens.semantic.css`'s light-mode text/border
 * roles; never a competing source of truth — the CSS custom property always
 * wins once a real stylesheet is applied.
 */
const FALLBACK_THEME: ChartTheme = {
  mobile: "#2a78d6",
  telegram: "#eb6834",
  grid: "#eceef1",
  axis: "#4a5563",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d9dde3",
  tooltipFg: "#171b21",
};

function readChartTheme(): ChartTheme {
  const computed = getComputedStyle(document.documentElement);
  const result = { ...FALLBACK_THEME };
  for (const key of Object.keys(CHART_TOKEN_VARS) as (keyof ChartTheme)[]) {
    const value = computed.getPropertyValue(CHART_TOKEN_VARS[key]).trim();
    if (value.length > 0) {
      (result as Record<keyof ChartTheme, string>)[key] = value;
    }
  }
  return result;
}

/**
 * Resolves the chart component tokens (`tokens.component.css`) to concrete
 * color strings for Recharts. Recharts renders `stroke`/`fill` as plain SVG
 * attributes rather than CSS declarations, so this reads the *computed*
 * custom-property value instead of passing a `var(...)` string through
 * un-resolved. Re-reads whenever `<html data-theme>` changes (`ThemeToggle`,
 * `src/lib/theme.ts`) so a chart already on screen re-colors without a
 * remount.
 */
export function useChartTheme(): ChartTheme {
  const [theme, setTheme] = useState<ChartTheme>(() =>
    typeof document === "undefined" ? FALLBACK_THEME : readChartTheme(),
  );

  useEffect(() => {
    setTheme(readChartTheme());
    const observer = new MutationObserver(() => {
      setTheme(readChartTheme());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return (): void => observer.disconnect();
  }, []);

  return theme;
}
